import { LitElement, html, nothing } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { icon } from '@Root/lib/lit-3form-block/icons';
import { getPageVariable } from '@Helpers/helpers';
import { BitreelFrameClient } from '@Helpers/bitreel-frame-client';
import {
  DEFAULT_MATERIAL,
  DEFAULT_OPACITY,
  DEFAULT_PANELS,
  EDITOR_PREFIX,
  EMBED_IMAGE_FIT,
  QUERY,
  defaultOrientationFor,
  embedImageFor,
  formatPlacement,
  imageNumberFromLink,
  layoutConfigFor,
  layoutsFor,
  opacityLevelFrom,
  parsePlacement,
  urlAtFit,
} from '@Helpers/digital-print-designer';
import { handleFileDownloadEvent } from '../../../../helpers/adobe-analytics';
import { createToolAnalytics } from '../../../../helpers/tool-analytics';
import { styles as designerStyles } from '../graphic-pattern-designer-barrel/css';
import { styles } from './css';

import '@Root/lib/lit-3form-block/components/cta-button';

import {
  SHARED_CLASSES,
  SHARED_ELEMENTS,
} from '@Root/lib/lit-3form-block/styles.js';
import { LIT_CSS_VARIABLES, GRID_LAYOUT } from '@Root/lib/lit-styles.js';
import { a11yStyles } from '../../../form-controls/a11y-styles';

const TOOL_NAME = 'Digital Print Designer';
const CONFIGURE_PATH = '/digital-printing/configure';

// A placement gesture streams, and Safari throws once a page calls
// replaceState 100 times in a few seconds, so the URL waits for a pause.
const QUERY_SYNC_DELAY_MS = 300;

// Uploads go to the browser's own memory and on to WebGL, so very large
// files stall phones before they help anyone.
const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

const library = json => (json ? { kind: 'library', ...json } : null);

const hasFiles = event =>
  Array.from(event.dataTransfer?.types || []).includes('Files');

export class DigitalPrintDesignerBarrel extends LitElement {
  static get properties() {
    return {
      content: { type: Object },
      _material: { type: String },
      _opacity: { type: Number },
      _orientation: { type: String },
      _panels: { type: Number },
      _showProps: { type: Boolean },
      _image: { type: Object },
      _starting: { type: Boolean },
      _dragging: { type: Boolean },
      _editorStatus: { type: String },
      _notice: { type: String },
      _downloading: { type: Boolean },
      _quoting: { type: Boolean },
    };
  }

  static get styles() {
    return [
      LIT_CSS_VARIABLES,
      SHARED_ELEMENTS,
      SHARED_CLASSES,
      GRID_LAYOUT,
      a11yStyles,
      designerStyles,
      styles,
    ];
  }

  constructor() {
    super();

    this.content = JSON.parse(this.getAttribute('content') || '{}');
    // Shared with Walter's 3D slide, which reports the viewer's gestures.
    this.tracker = createToolAnalytics(TOOL_NAME);
    this._initialized = false;
    this._material = DEFAULT_MATERIAL;
    this._opacity = DEFAULT_OPACITY;
    this._orientation = 'portrait';
    this._panels = DEFAULT_PANELS;
    this._showProps = true;
    this._image = null;
    this._starting = true;
    this._dragging = false;
    this._placement = null;
    this._editorStatus = 'idle';
    this._notice = '';
    this._downloading = false;
    this._quoting = false;
    this._editor = null;
    this.onStrayDrop = this.onStrayDrop.bind(this);
  }

  // Inside Walter the element is upgraded before Lit commits the bound
  // content attribute, so derived state has to wait for the data to land.
  willUpdate(changedProperties) {
    if (
      !this._initialized &&
      changedProperties.has('content') &&
      this.content?.digital_print_designer
    ) {
      this._initialized = true;
      this.initializeFromContent();
    }
  }

  updated() {
    this.attachEditor();
  }

  // Walter can move the barrel; a reconnect gets the editor back, and the
  // upload's URL is kept because the carousel shows it too.
  connectedCallback() {
    super.connectedCallback();
    window.addEventListener('dragover', this.onStrayDrop);
    window.addEventListener('drop', this.onStrayDrop);
    if (this._initialized) this.attachEditor();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('dragover', this.onStrayDrop);
    window.removeEventListener('drop', this.onStrayDrop);
    clearTimeout(this._querySyncTimeout);
    this._editor?.dispose();
    this._editor = null;
  }

  async initializeFromContent() {
    const params = new URLSearchParams(window.location.search);
    const levels = this.designer.opacity_levels || [];

    const material = params.get(QUERY.material);
    if (this.materials.some(option => option.key === material)) {
      this._material = material;
    }
    const level = opacityLevelFrom(levels, params.get(QUERY.opacity));
    if (level) this._opacity = level.level;
    this._showProps = params.get(QUERY.props) !== '0';

    this._image = await this.startingImage(params);
    this._starting = false;
    this.applyLayoutParams(params);
    this._placement = parsePlacement(params.get(QUERY.placement));

    // A gallery link moves to the image's own URL; otherwise the URL only
    // changes once the visitor changes something.
    if (params.has(QUERY.imageLink)) this.writeQueryParams();
    this.tracker.observe(this);
    this.sendToEditor({ image: true, layout: true, placement: true });
    this.emitSelection(['material', 'opacity', 'layout', 'showProps']);
  }

  // A per-image page hands its image over; a gallery link carries only the
  // file URL; otherwise the barrel's default.
  async startingImage(params) {
    const shared = getPageVariable('digitalPrintImage');
    if (shared) return library(shared);

    const link = params.get(QUERY.imageLink);
    if (link) {
      const imageNumber = imageNumberFromLink(link);
      const found = imageNumber && (await this.lookUpImage(imageNumber));
      if (found) return library(found);

      return {
        kind: 'link',
        src: link,
        orientation: params.get(QUERY.orientation),
      };
    }

    return library(this.designer.default_image);
  }

  async lookUpImage(imageNumber) {
    const {
      image_lookup_path: lookupPath,
      image_lookup_placeholder: placeholder,
    } = this.designer;
    if (!lookupPath || !placeholder) return null;
    const path = lookupPath.replace(
      placeholder,
      encodeURIComponent(imageNumber)
    );

    try {
      const response = await fetch(path, {
        headers: { Accept: 'application/json' },
      });
      return response.ok ? await response.json() : null;
    } catch (error) {
      return null;
    }
  }

  applyLayoutParams(params) {
    const layout = params.get(QUERY.layout);
    const available = this.layouts.map(option => option.orientation);
    this._orientation = available.includes(layout)
      ? layout
      : defaultOrientationFor(this._image);

    const panels = Number(params.get(QUERY.panels));
    if (this.panelOptions.includes(panels)) this._panels = panels;
  }

  // ---------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------

  get designer() {
    return this.content?.digital_print_designer || {};
  }

  get materials() {
    return this.designer.materials || [];
  }

  get opacityLevels() {
    return this.designer.opacity_levels || [];
  }

  // Only what the quote can store, so a rep always gets the file.
  get uploadTypes() {
    return this.designer.upload_content_types || [];
  }

  get layouts() {
    return layoutsFor(this.designer, this._image);
  }

  get selectedLayout() {
    return (
      this.layouts.find(option => option.orientation === this._orientation) ||
      this.layouts[0] ||
      null
    );
  }

  get panelOptions() {
    const multi = (this.designer.layouts || []).find(
      option => option.orientation === 'multi'
    );
    return multi?.panels || [];
  }

  get isUpload() {
    return this._image?.kind === 'upload';
  }

  get imageNumber() {
    return this._image?.imageNumber || null;
  }

  get imageTitle() {
    if (!this._image) return '';
    if (this.isUpload) return this._image.name;
    return this._image.title || this.imageNumber || '';
  }

  get layoutConfig() {
    return layoutConfigFor({
      orientation: this.selectedLayout?.orientation || this._orientation,
      panels: this._panels,
    });
  }

  get opacityLabel() {
    const level = this.opacityLevels.find(
      option => option.level === this._opacity
    );
    return level?.label || String(this._opacity);
  }

  get materialLabel() {
    const material = this.materials.find(
      option => option.key === this._material
    );
    return material?.label || this._material;
  }

  get layoutLabel() {
    const layout = this.selectedLayout;
    if (!layout) return '';
    if (layout.orientation !== 'multi') return layout.label;
    return [layout.label, this._panels, 'Panels'].join(' ');
  }

  get panelSize() {
    const layout = this.selectedLayout;
    if (!layout) return '';
    // A multi-panel layout's width is per panel.
    const width =
      layout.orientation === 'multi'
        ? layout.max_width_in * this._panels
        : layout.max_width_in;
    return ['Up to', width + '"', '×', layout.max_height_in + '"'].join(' ');
  }

  // The configuration as the viewer takes it (CLIENT_INTEGRATION.md).
  get viewerConfig() {
    return {
      material: this._material,
      opacity: this._opacity,
      layout: this.layoutConfig,
      showProps: this._showProps,
      image: embedImageFor(this._image),
      placement: this._placement,
    };
  }

  // What reports call this configuration.
  get analyticsLabel() {
    return [
      this.isUpload ? 'Customer Image' : this.imageNumber,
      this.materialLabel,
      this.layoutLabel,
    ]
      .filter(Boolean)
      .join(' ');
  }

  // Everything a rep needs to reproduce the design.
  get leadDetails() {
    return {
      imageName: this.isUpload ? '' : this.imageTitle,
      imageNumber: this.imageNumber || '',
      imageUrl: this.isUpload ? '' : this.sourceImageUrl,
      customerSuppliedImage: this.isUpload,
      customerImageName: this.isUpload ? this._image.name : '',
      material: this.materialLabel,
      opacity: this.opacityLabel,
      layout: this.layoutLabel,
      showProps: this._showProps,
      placement: formatPlacement(this._placement) || '',
    };
  }

  get sourceImageUrl() {
    if (!this._image || this.isUpload) return '';
    return urlAtFit(this._image.src, EMBED_IMAGE_FIT);
  }

  // ---------------------------------------------------------------------
  // URL state
  // ---------------------------------------------------------------------

  syncQueryParams() {
    clearTimeout(this._querySyncTimeout);
    this._querySyncTimeout = setTimeout(
      () => this.writeQueryParams(),
      QUERY_SYNC_DELAY_MS
    );
  }

  // Library images live at their own URL; uploads have nothing to share, so
  // they stay on the designer's own path.
  writeQueryParams() {
    const url = new URL(window.location.href);
    const params = url.searchParams;

    if (this.isUpload) {
      url.pathname = CONFIGURE_PATH;
    } else if (this.imageNumber) {
      url.pathname = [CONFIGURE_PATH, this.imageNumber].join('/');
    }
    if (this.isUpload || this.imageNumber) {
      params.delete(QUERY.imageLink);
      params.delete(QUERY.orientation);
      params.delete('scale');
    }

    params.set(QUERY.material, this._material);
    params.set(QUERY.opacity, this.opacityLabel);
    params.set(QUERY.layout, this.layoutConfig.orientation);
    if (this.layoutConfig.panels) {
      params.set(QUERY.panels, String(this.layoutConfig.panels));
    } else {
      params.delete(QUERY.panels);
    }
    if (this._showProps) params.delete(QUERY.props);
    else params.set(QUERY.props, '0');

    const placement = formatPlacement(this._placement);
    if (placement) params.set(QUERY.placement, placement);
    else params.delete(QUERY.placement);

    window.history.replaceState(window.history.state, '', url);
  }

  // ---------------------------------------------------------------------
  // Placement editor
  // ---------------------------------------------------------------------

  get editorUrl() {
    return this.designer.embeds?.editor_url || null;
  }

  attachEditor() {
    if (this._editor || !this.editorUrl) return;
    const iframe = this.renderRoot.querySelector('.editor-frame');
    if (!iframe) return;

    this._editor = new BitreelFrameClient({
      url: this.editorUrl,
      prefix: EDITOR_PREFIX,
    });
    this._editor.on('placement-changed', detail =>
      this.onPlacementChanged(detail)
    );
    this._editor.onChange(client => {
      this._editorStatus = client.lastError ? 'error' : client.status;
    });
    this._editor.attach(iframe);
    this._editorStatus = this._editor.status;
    this.sendToEditor({ image: true, layout: true, placement: true });
  }

  // A new image or layout sent without a placement makes the editor fit the
  // image again, so placement only rides along when it's meant to.
  sendToEditor({ image = false, layout = false, placement = false }) {
    if (!this._editor) return;
    const config = {};
    if (image) config.image = embedImageFor(this._image);
    if (layout) config.layout = this.layoutConfig;
    if (placement) config.placement = this._placement;
    this._editor.setConfig(config);
  }

  onPlacementChanged(detail) {
    this._placement = detail?.placement || null;
    this.emitSelection(['placement']);
    // The editor's own fit after a new image or layout isn't a visitor edit.
    if (!detail?.final || detail.source === 'fit') return;

    this.syncQueryParams();
    this.tracker.placementChanged(detail);
  }

  // ---------------------------------------------------------------------
  // Selection changes
  // ---------------------------------------------------------------------

  // Walter forwards only the changed fields to the viewer, so an unchanged
  // image isn't sent again on every placement.
  emitSelection(changed) {
    this.dispatchEvent(
      new CustomEvent('digitalPrintDesignerUpdated', {
        bubbles: true,
        composed: true,
        detail: {
          changed,
          config: this.viewerConfig,
          image: this._image,
          imageTitle: this.imageTitle,
          analyticsLabel: this.analyticsLabel,
          lead: this.leadDetails,
        },
      })
    );
  }

  changed(fields, facet, value) {
    this.syncQueryParams();
    this.emitSelection(fields);
    if (facet) this.tracker.select(facet, value);
  }

  selectMaterial(key) {
    if (key === this._material) return;
    this._material = key;
    this.changed(['material'], 'Material', key);
  }

  selectOpacity(level) {
    if (level.level === this._opacity) return;
    this._opacity = level.level;
    this.changed(['opacity'], 'Opacity', level.label);
  }

  selectLayout(orientation) {
    if (orientation === this.layoutConfig.orientation) return;
    this._orientation = orientation;
    this._placement = null;
    this.sendToEditor({ layout: true });
    this.changed(['layout', 'placement'], 'Layout', orientation);
  }

  selectPanels(panels) {
    if (panels === this._panels) return;
    this._panels = panels;
    this._placement = null;
    this.sendToEditor({ layout: true });
    this.changed(['layout', 'placement'], 'Panels', String(panels));
  }

  selectProps(showProps) {
    if (showProps === this._showProps) return;
    this._showProps = showProps;
    this.changed(['showProps'], 'Props', showProps ? 'on' : 'off');
  }

  onUploadChange(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) this.useUpload(file);
  }

  // Text, links and images dragged from another tab carry no file to use.
  onUploadDragOver(event) {
    if (!hasFiles(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    this._dragging = true;
  }

  // The box's children ignore the pointer, so every dragleave is the box's
  // own; Safari leaves relatedTarget null here.
  onUploadDragLeave() {
    this._dragging = false;
  }

  onUploadDrop(event) {
    event.preventDefault();
    this._dragging = false;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.useUpload(file);
  }

  // A file dropped anywhere else would replace the page, and the design
  // with it. File inputs keep their own drop.
  onStrayDrop(event) {
    if (event.defaultPrevented || !hasFiles(event)) return;
    if (event.composedPath()[0]?.type === 'file') return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'none';
  }

  useUpload(file) {
    if (!this.uploadTypes.includes(file.type)) {
      this._notice = this.uploadTypeNotice;
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      this._notice = 'Choose an image under 25 MB.';
      return;
    }

    this._notice = '';
    this.releaseUpload(this._image);
    this._image = {
      kind: 'upload',
      name: file.name,
      blob: file,
      objectUrl: URL.createObjectURL(file),
    };
    this._orientation = defaultOrientationFor(this._image);
    this._placement = null;
    this.sendToEditor({ image: true, layout: true });
    this.changed(['image', 'layout', 'placement']);
    this.tracker.upload('Image');
  }

  releaseUpload(image) {
    if (image?.objectUrl) URL.revokeObjectURL(image.objectUrl);
  }

  chooseFromGallery() {
    this.tracker.action('Choose from Gallery', this.analyticsLabel);
  }

  downloadConfiguredImage(event) {
    event.preventDefault();
    if (this._downloading) return;
    this._downloading = true;
    this.dispatchEvent(
      new CustomEvent('downloadConfiguredImageFromBarrel', {
        bubbles: true,
        composed: true,
        detail: {
          done: () => {
            this._downloading = false;
          },
        },
      })
    );
  }

  downloadSourceImage() {
    handleFileDownloadEvent({
      file_format: 'JPG',
      file_name: ['3FORM', this.imageNumber].join('-'),
      link_url: this.sourceImageUrl,
      placement: TOOL_NAME,
    });
  }

  // The button carries no URL so it cannot navigate on its own; Walter
  // captures and uploads the design for the quote form, records the NAV
  // event and then leaves the page, which takes a few seconds.
  requestAQuote() {
    if (this._quoting) return;
    this._quoting = true;
    // Walter puts the page URL in the quote, so a pending sync lands first.
    clearTimeout(this._querySyncTimeout);
    this.writeQueryParams();
    this.tracker.action('Request a Quote', this.analyticsLabel);
    this.dispatchEvent(
      new CustomEvent('requestMoreInfoFromBarrel', {
        bubbles: true,
        composed: true,
        detail: {
          cta: 'Request a Quote',
          url: '/request-quote',
          analyticsLabel: this.analyticsLabel,
          lead: this.leadDetails,
          digitalPrint: {
            config: this.viewerConfig,
            image: this._image,
          },
          done: () => {
            this._quoting = false;
          },
        },
      })
    );
  }

  // The button opens the rep modal itself; this only records it.
  contactARep() {
    this.tracker.action('Contact a Rep', this.analyticsLabel);
  }

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  get title() {
    if (this.isStart) return 'GET STARTED';
    return (this.content.title || 'Design Your Panel').toUpperCase();
  }

  // With no image there's nothing to configure yet, so the barrel only
  // offers ways to pick one.
  get isStart() {
    return !this._starting && !this._image;
  }

  get uploadFormats() {
    return this.uploadTypes
      .map(type => type.replace('image/', '').replace('jpeg', 'jpg'))
      .map(format => format.toUpperCase());
  }

  get uploadTypeNotice() {
    const formats = this.uploadFormats;
    const last = formats.pop();
    const list = formats.length
      ? [formats.join(', '), last].join(' or ')
      : last;
    return ['Choose a', list, 'image.'].join(' ');
  }

  render() {
    return html`
      <div class="barrel-wrapper print-none">
        <div class="barrel-header">
          <span class="barrel-heavy-text type-barrel-title">
            <span>${this.title}</span>
          </span>
        </div>
        ${this._starting
          ? nothing
          : this.isStart
          ? this.startHTML
          : this.designerHTML}
      </div>
    `;
  }

  get designerHTML() {
    return html`
      <div class="sections">
        ${this.imageSectionHTML} ${this.editorSectionHTML}
        ${this.materialSectionHTML} ${this.opacitySectionHTML}
        ${this.layoutSectionHTML} ${this.propsSectionHTML}
      </div>
      <p class="rep-note">
        Want to customize further options? Get in touch with a rep.
      </p>
      <div class="cta-row">
        <cta-button
          .ctaData=${{
            text: this._quoting ? 'Preparing Quote…' : 'Request a Quote',
            standardWidth: true,
          }}
          aria-busy=${this._quoting ? 'true' : 'false'}
          cta-type="primary"
          @click="${this.requestAQuote}"
        ></cta-button>
        <cta-button
          cta-data=${JSON.stringify({
            text: 'Contact a Rep',
            url: '#contact-rep',
            standardWidth: true,
          })}
          cta-type="secondary"
          @click="${this.contactARep}"
        ></cta-button>
      </div>
      ${this.downloadsHTML}
    `;
  }

  get startHTML() {
    return html`
      <div class="sections">
        <div class="section">
          <p class="start-intro">
            Use your own artwork, logo, color, or brand elements to completely
            customize your project.
          </p>
        </div>
        <div class="section">
          <p class="section-label">Upload Your Image</p>
          <label
            class=${classMap({
              'upload-box': true,
              'is-dragging': this._dragging,
            })}
            @dragover="${this.onUploadDragOver}"
            @dragleave="${this.onUploadDragLeave}"
            @drop="${this.onUploadDrop}"
          >
            <span class="upload-box__icon">${icon('image-add')}</span>
            <span class="upload-box__title" id="upload-box-title">
              Add your custom image
            </span>
            <span class="upload-box__hint">Drag & drop or click to browse</span>
            <span class="upload-box__formats" id="upload-box-formats">
              Supported formats: ${this.uploadFormats.join(', ')}
            </span>
            <span class="image-action">Browse Files</span>
            <span class="upload-box__note" id="upload-box-note">
              Your image will be displayed in the 3D viewer. You can then adjust
              its placement, opacity, and other panel options.
            </span>
            <input
              class="visually-hidden"
              type="file"
              accept=${this.uploadTypes.join(',')}
              aria-labelledby="upload-box-title"
              aria-describedby="upload-box-formats upload-box-note"
              @change="${this.onUploadChange}"
            />
          </label>
          ${this._notice
            ? html`
                <p class="status-note" role="alert">${this._notice}</p>
              `
            : nothing}
        </div>
        <div class="section">
          <p class="section-label">Or Start From Our Gallery</p>
          <div class="image-actions">
            <a
              class="image-action"
              href=${this.designer.gallery_path || '/image-gallery-portfolio'}
              @click="${this.chooseFromGallery}"
            >
              Choose from Gallery
            </a>
          </div>
        </div>
      </div>
    `;
  }

  segmentHTML({ label, selected, onSelect }) {
    return html`
      <button
        type="button"
        class=${classMap({ segment: true, 'is-selected': selected })}
        aria-pressed=${selected ? 'true' : 'false'}
        @click="${onSelect}"
      >
        ${label}
      </button>
    `;
  }

  get imageSectionHTML() {
    const description = this.isUpload ? '' : this._image?.description;
    return html`
      <div class="section">
        <p class="section-label">Image</p>
        ${this._image
          ? html`
              <div class="image-summary">
                <p class="image-summary__title">${this.imageTitle}</p>
                ${description
                  ? html`
                      <p class="image-summary__description">${description}</p>
                    `
                  : nothing}
              </div>
            `
          : nothing}
        <div class="image-actions">
          <a
            class="image-action"
            href=${this.designer.gallery_path || '/image-gallery-portfolio'}
            @click="${this.chooseFromGallery}"
          >
            Choose from Gallery
          </a>
          <label class="image-action">
            Upload Your Own
            <input
              class="visually-hidden"
              type="file"
              accept=${this.uploadTypes.join(',')}
              @change="${this.onUploadChange}"
            />
          </label>
        </div>
        ${this._notice
          ? html`
              <p class="status-note" role="alert">${this._notice}</p>
            `
          : nothing}
      </div>
    `;
  }

  get editorSectionHTML() {
    if (!this.editorUrl || !this._image) return nothing;
    return html`
      <div class="section">
        <p class="section-label">Placement</p>
        <iframe class="editor-frame" title="Image placement editor"></iframe>
        ${this._editorStatus === 'error'
          ? html`
              <p class="status-note" role="alert">
                The placement editor couldn't load this image.
              </p>
            `
          : html`
              <p class="status-note">
                Drag, resize or rotate the image on the panel.
              </p>
            `}
      </div>
    `;
  }

  get materialSectionHTML() {
    if (this.materials.length === 0) return nothing;
    return html`
      <div class="section">
        <p class="section-label">Material</p>
        <div class="segments" role="group" aria-label="Material">
          ${this.materials.map(option =>
            this.segmentHTML({
              label: option.label,
              selected: this._material === option.key,
              onSelect: () => this.selectMaterial(option.key),
            })
          )}
        </div>
      </div>
    `;
  }

  get opacitySectionHTML() {
    if (this.opacityLevels.length === 0) return nothing;
    return html`
      <div class="section">
        <p class="section-label">Opacity Level</p>
        <div class="range-labels" aria-hidden="true">
          <span>Less Opaque</span>
          <span>More Opaque</span>
        </div>
        <div class="segments is-full" role="group" aria-label="Opacity Level">
          ${this.opacityLevels.map(level =>
            this.segmentHTML({
              label: level.label,
              selected: this._opacity === level.level,
              onSelect: () => this.selectOpacity(level),
            })
          )}
        </div>
      </div>
    `;
  }

  get layoutSectionHTML() {
    if (this.layouts.length === 0) return nothing;
    const isMulti = this.layoutConfig.orientation === 'multi';
    return html`
      <div class="section">
        <p class="section-label">Orientation</p>
        <div class="segments is-full" role="group" aria-label="Orientation">
          ${this.layouts.map(option =>
            this.segmentHTML({
              label: option.label,
              selected: this.layoutConfig.orientation === option.orientation,
              onSelect: () => this.selectLayout(option.orientation),
            })
          )}
        </div>
        ${isMulti
          ? html`
              <div class="segments" role="group" aria-label="Panels">
                ${this.panelOptions.map(panels =>
                  this.segmentHTML({
                    label: String(panels),
                    selected: this._panels === panels,
                    onSelect: () => this.selectPanels(panels),
                  })
                )}
              </div>
            `
          : nothing}
        <p class="panel-size">${this.panelSize}</p>
      </div>
    `;
  }

  get propsSectionHTML() {
    return html`
      <div class="section">
        <p class="section-label">Props</p>
        <div class="segments" role="group" aria-label="Props">
          ${this.segmentHTML({
            label: 'Show',
            selected: this._showProps,
            onSelect: () => this.selectProps(true),
          })}
          ${this.segmentHTML({
            label: 'Hide',
            selected: !this._showProps,
            onSelect: () => this.selectProps(false),
          })}
        </div>
      </div>
    `;
  }

  // Walter does the configured export, the same as the carousel's own
  // button. A customer's own file needs no download back.
  get downloadsHTML() {
    return html`
      <div class="download-links">
        <a
          href="#"
          class="download-link"
          aria-busy=${this._downloading ? 'true' : 'false'}
          @click="${this.downloadConfiguredImage}"
        >
          <span class="download-link__icon">${icon('download')}</span>
          <span class="download-link__text">
            ${this._downloading
              ? 'Preparing Image…'
              : 'Download Image as Configured'}
          </span>
        </a>
        ${this.sourceImageUrl
          ? html`
              <a
                href=${this.sourceImageUrl}
                class="download-link"
                target="_blank"
                rel="noopener"
                download
                @click="${this.downloadSourceImage}"
              >
                <span class="download-link__icon">${icon('download')}</span>
                <span class="download-link__text">Download Source Image</span>
              </a>
            `
          : nothing}
      </div>
    `;
  }
}

customElements.define(
  'digital-print-designer-barrel',
  DigitalPrintDesignerBarrel
);
